import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { Plus, Pencil, Trash2, X, HelpCircle, GripVertical } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { Toaster } from '../../components/ui/sonner';

const AdminFAQs = () => {
  const { token } = useAuth();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ question: '', answer: '' });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/faqs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setFaqs(await response.json());
    } catch (error) {
      toast.error('Erro ao carregar FAQs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingItem 
        ? `${API_URL}/api/admin/faqs/${editingItem.id}`
        : `${API_URL}/api/admin/faqs`;
      
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingItem ? 'FAQ atualizada!' : 'FAQ criada!');
        setShowModal(false);
        resetForm();
        fetchData();
      }
    } catch (error) {
      toast.error('Erro ao salvar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja eliminar esta FAQ?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/faqs/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('FAQ eliminada!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao eliminar');
    }
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({ question: item.question, answer: item.answer });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({ question: '', answer: '' });
  };

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Perguntas Frequentes</h1>
            <p className="text-gray-500">Gerencie as FAQs do site</p>
          </div>
          <Button onClick={() => { setShowModal(true); resetForm(); }} className="bg-purple-600 hover:bg-purple-700">
            <Plus size={18} className="mr-2" />
            Nova FAQ
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <div className="space-y-3">
            {faqs.map((item, index) => (
              <Card key={item.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex items-center space-x-2 text-gray-400">
                      <GripVertical size={18} />
                      <span className="text-sm font-medium">{index + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3">
                          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                            <HelpCircle className="text-purple-600" size={20} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{item.question}</h3>
                            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{item.answer}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
                          <Button variant="ghost" size="sm" onClick={() => openEditModal(item)}>
                            <Pencil size={16} />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(item.id)}>
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {faqs.length === 0 && (
              <div className="text-center py-12 text-gray-500">Nenhuma FAQ encontrada</div>
            )}
          </div>
        )}

        <p className="text-sm text-gray-500">Total: {faqs.length} FAQs</p>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{editingItem ? 'Editar FAQ' : 'Nova FAQ'}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}><X size={18} /></Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Pergunta *</Label>
                  <Input value={formData.question} onChange={(e) => setFormData({ ...formData, question: e.target.value })} placeholder="Ex: Qual o prazo de garantia?" required />
                </div>
                <div>
                  <Label>Resposta *</Label>
                  <Textarea value={formData.answer} onChange={(e) => setFormData({ ...formData, answer: e.target.value })} rows={5} required placeholder="Resposta completa para a pergunta..." />
                </div>
                <div className="flex space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">Cancelar</Button>
                  <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700">{editingItem ? 'Atualizar' : 'Criar'}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminFAQs;
